/**
 * StorageBanner — workspace-level warning that object storage is unreachable.
 *
 * Shown when /api/health reports `storage.ok === false`. While the banner is
 * up:
 *   • New uploads will fail (the presign step itself works, but the browser-
 *     to-MinIO PUT will time out).
 *   • Existing document previews and downloads will fail (presigned GETs
 *     return 503 or DNS-resolve fail).
 *
 * The polling is handled by useSystemHealth in lib/system-health.ts; this
 * component only renders.
 *
 * Positioned above the rest of the page (sibling to RefreshBanner) so that
 * the warning is unmissable even on tabs where uploads aren't the main task.
 */

import { AlertTriangle } from 'lucide-react';
import { describeStorageFailure, useSystemHealth } from '@/lib/system-health';

export function StorageBanner() {
  const q = useSystemHealth();
  const storage = q.data?.storage;
  if (!storage || storage.ok) return null;

  return (
    <div
      role="alert"
      aria-live="polite"
      className="flex items-center gap-3 border-b border-status-danger/20 bg-status-danger-bg px-6 py-2 text-sm text-status-danger"
    >
      <AlertTriangle className="h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <span className="font-medium">Document storage is unavailable</span>{' '}
        <span className="text-secondary">
          ({describeStorageFailure(storage.reason)}). New uploads and document downloads will
          fail until this is restored. Communications can still be logged. Contact your
          administrator to check the storage service.
        </span>
      </div>
    </div>
  );
}
